import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Building2, Rocket, User as UserIcon, Check, ExternalLink, Tag, Briefcase, Zap, ShieldCheck } from "lucide-react";
import WishlistButton from "./WishlistButton";
import ShareButton from "./ShareButton";
import VerifiedBadge from "./VerifiedBadge";
import ProtectedImage from "./ProtectedImage";
import SaveCandidateButton from "./SaveCandidateButton";
import { formatPrice, formatCompensationRange } from "../lib/currency";
import { shouldShowConditionTag } from "../lib/categoryRules";
import { discountPercent } from "../lib/pricingLabels";
import { cdnImage } from "../lib/imgproxy";
import { api } from "../lib/api";

function isBoosted(listing) {
    if (!listing.boosted_until) return false;
    return new Date(listing.boosted_until).getTime() > Date.now();
}

/**
 * v77.30 — LinkedIn-profile-style card for Open-for-Work listings.
 *
 * Replaces the 4:3 commerce tile with a profile-shaped card:
 *   - Top: yellow "OPEN TO WORK" banner ribbon
 *   - Middle: avatar (left, 72px circle) + name + headline (right)
 *   - Skills row (top 3 chips), location + years of experience
 *   - Bottom: "Available" badge + "Resume" / "Open to relocate" pills
 *
 * Stays the same height as siblings via `h-full` + `auto-rows-fr` on the
 * parent grid (matches the rule in the main card).
 */
function JobSeekerCard({ listing }) {
    const img = listing.images?.[0];
    const years = listing.meta?.years_of_experience;
    const months = listing.meta?.months_of_experience;
    const isDemo = !!listing.is_demo;
    // v79.190 — Founder direction (Feb 2026): "no need to show open
    // for work tag if job seeker did not select the option." The
    // ribbon now requires an EXPLICIT truthy flag on the candidate's
    // meta. If the field is missing or falsy the tile shows no
    // ribbon at all — quiet by default, loud only when the candidate
    // has actively opted in.
    const openToWork = listing.meta?.open_to_work === true;

    const expLabel = (() => {
        if (years == null && months == null) return null;
        const y = Number(years) || 0;
        const m = Number(months) || 0;
        if (y === 0 && m === 0) return "Fresher";
        const parts = [];
        if (y > 0) parts.push(`${y}y`);
        if (m > 0) parts.push(`${m}m`);
        return `${parts.join(" ")} exp`;
    })();

    return (
        <Link
            to={`/listing/${listing.id}`}
            data-testid={`product-card-${listing.id}`}
            className="group relative flex flex-col bg-white rounded-xl border border-aduku-border aduku-card-lift overflow-hidden hover:border-aduku-navy/40 [content-visibility:auto] [contain-intrinsic-size:270px]"
        >
            {/* v79.189 — OPEN TO WORK banner is now conditional on the
                candidate's explicit `open_to_work` meta flag. Hidden
                when the candidate has toggled themselves off (not
                actively looking) so recruiters see accurate signals. */}
            {openToWork && (
                <div
                    data-testid={`js-open-to-work-${listing.id}`}
                    className="bg-aduku-yellow text-aduku-navy text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-center py-1.5 border-b border-aduku-yellow-600/30"
                >
                    Open to work
                </div>
            )}
            {isDemo && (
                <div className={`absolute ${openToWork ? "top-7" : "top-2"} left-2 z-10 pointer-events-none`}>
                    <span data-testid="badge-demo" className="inline-flex items-center px-2 py-0.5 rounded-md bg-orange-500 text-white text-[11px] font-black uppercase tracking-wider shadow-sm" title="Seeded demo candidate — not a real applicant">
                        Demo
                    </span>
                </div>
            )}
            <div className={`absolute ${openToWork ? "top-9" : "top-2"} right-2 z-10`}>
                <SaveCandidateButton listingId={listing.id} variant="compact" />
            </div>
            {/* v79.189 — Founder direction: "photos should align proper
                without gaps". Redesigned the tile so the passport photo
                fills the top ~62% of the card as a rectangular hero
                image (object-cover, center-top anchor so the face
                stays visible). Name + location + experience sit below
                on a compact, tight strip — no gradient background, no
                circular avatar wasting vertical space. */}
            <div className="relative w-full h-[178px] bg-aduku-bg overflow-hidden flex-shrink-0">
                {img ? (
                    <ProtectedImage
                        src={cdnImage(img, 480)}
                        alt={listing.title}
                        objectFit="cover"
                        objectPosition="center top"
                        className="w-full h-full"
                        testId={`protected-tile-img-${listing.id}`}
                        watermark={false}
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center bg-aduku-yellow/30 text-aduku-navy font-display font-black text-4xl">
                        {(listing.seller_name || listing.title || "?").charAt(0).toUpperCase()}
                    </div>
                )}
            </div>
            <div className="flex flex-col px-3 py-2 min-h-0">
                <div className="font-display font-bold text-aduku-navy text-sm leading-tight line-clamp-1 break-words" title={listing.title}>
                    {listing.title}
                </div>
                <div className="text-[11px] text-aduku-navy/65 mt-0.5 inline-flex items-center gap-1 max-w-full">
                    <MapPin className="w-3 h-3 shrink-0" />
                    <span className="truncate">{listing.city}{listing.state ? `, ${listing.state}` : ""}</span>
                </div>
                {expLabel && (
                    <div className="text-[11px] text-aduku-navy/75 mt-0.5 inline-flex items-center gap-1 font-semibold">
                        <Briefcase className="w-3 h-3" /> {expLabel}
                    </div>
                )}
            </div>
        </Link>
    );
}

// v78.36 — Wrap in React.memo so Browse / Home / Shop re-renders on
// scroll don't remount every visible card. Before this fix: a few
// seconds of scrolling reloaded ~1,600 images (image proxy spam +
// Safari flicker). After: zero unnecessary reloads. The default
// shallow-equal check on the single `listing` prop is sufficient
// because parent components pass stable references (api responses
// are immutable; state arrays are replaced wholesale, not mutated).
function ProductCard({ listing }) {
    const img = listing.images?.[0];
    const isBusiness = listing.seller_account_type === "business";
    const isAffiliate = !!listing.is_affiliate;
    const isDemo = !!listing.is_demo;
    // v79.200 — Founder direction (Feb 2026): "No need for New/Used
    // badges on sponsored ads — remove completely. Portal auto-adjust
    // badging + remove extra spaces — applies to all ads + fresh
    // listings." Sponsored tiles now hide the account + condition
    // chips so the tile is Image + Sponsored pill + Price only. The
    // `showAccountBadge` / `showConditionBadge` gate collapses the
    // whole badge row when both are false — no leftover empty strip.
    const showConditionBadge = !isAffiliate && shouldShowConditionTag(listing.category, listing.meta) && !!listing.condition;
    const showAccountBadge = !isAffiliate;

    // v77.30 — Open-for-Work tiles render as a profile-style card
    // (photo + name + headline + skills + "Available" badge) instead of
    // the commerce-shaped 4:3-image tile. Founder request: candidates
    // should feel like LinkedIn profiles, not like products on a shelf.
    if (listing.category === "job-seeker") {
        return <JobSeekerCard listing={listing} />;
    }

    // Affiliate (Sponsored Ads) behaviour — founder rule (v77.1):
    //   "Click, open in new tab, or any action must go DIRECTLY to the
    //    partner site, never to the Adukoo intermediate page."
    // Render the card as a real <a href={affiliate_url}> when the listing
    // is affiliate. That way:
    //   - left-click → browser navigates to the partner URL
    //   - middle-click / Cmd+click → opens partner URL in new tab
    //   - right-click → "Open in new tab" / "Copy link address" both
    //     hand the partner URL (not the Adukoo /listing/{id} path) — fixing
    //     the gap the older onClick-only handler left open.
    // We still fire the click-count beacon on left-click so admins can
    // track which sponsored deals convert.
    const affiliateClickBeacon = () => {
        if (!isAffiliate || !listing.affiliate_url) return;
        try { api.post(`/listings/${listing.id}/affiliate-click`).catch(() => {}); } catch { /* ignore */ }
    };

    // v78.35 — Founder complaint: extra whitespace in sponsored / business
    // tiles makes rows look unequal next to regular tiles. Root cause was
    // `h-full max-h-[380px]` + `auto-rows-fr`: rows stretched to the
    // TALLEST card and shorter affiliate cards (which skip the price /
    // seller / location blocks) ended up with empty whitespace below.
    //
    // New rule (founder-approved): EVERY ad tile renders the EXACT same
    // structural blocks, in the same order, with the same `min-h-*`. The
    // outer card is locked to a fixed pixel height per breakpoint so
    // cards from different sections (Sponsored / Business / Individual /
    // Digital Store catalog) drop into the same grid pixel-uniform.
    // `content-visibility: auto` stays for the v78.33 mobile crash fix.
    // v79.195 — Founder direction (Feb 2026): "Still lots of unnecessary
    // spaces on ad tiles. Fix on all categories. Alignment of badges
    // proper on ad tiles, same line. Price can take new line. All
    // categories, all sections, all pages. In case of no attributes
    // leave blank." Removed the fixed-height contract on the card so
    // it now sizes to actual content — no more 30-50px empty strip
    // under the price. Grid rows still align because `align-items:
    // stretch` (CSS default) equalises heights across a row, but the
    // baseline is now driven by the ad with the least content, not a
    // 260px minimum.
    const cardClassName = `group flex flex-col bg-white rounded-xl border aduku-card-lift overflow-hidden [content-visibility:auto] [contain-intrinsic-size:230px] ${isAffiliate ? "border-aduku-yellow ring-1 ring-aduku-yellow/60" : "border-aduku-border"}`;

    // v78.42 — performance fix. The previous version defined `CardShell`
    // as a nested component INSIDE ProductCard's render. Every parent
    // re-render created a brand-new CardShell function reference, which
    // React treated as a different component type and so destroyed +
    // remounted the ENTIRE card subtree (image, badges, title block).
    // On a Browse grid with 60+ visible cards that was tens of thousands
    // of unnecessary DOM mutations on every scroll-triggered parent
    // re-render — by far the biggest contributor to the perceived
    // sluggishness founder reported. Now we render the body ONCE into a
    // variable and conditionally wrap it with either <a> (affiliate) or
    // <Link> (regular). Pure JSX, zero new component types per render.
    const cardBody = (
        <>
            {/* v79.25 — Image switched from object-cover (crop-to-fill) to
                object-contain (show entire image, letterbox with a neutral
                bg). Founder request: "entire image portion is completely
            {/* v79.173 — Founder direction (again): "images should be
                shown completely" — reverted crop back to `object-contain`
                so no user-uploaded photo is ever cropped. To keep the
                tile edges looking clean (no white / grey side gaps),
                the SAME image is used as a blurred filler behind the
                sharp one. Any letterbox area therefore renders as a
                soft, tasteful blur of the photo itself — feels
                intentional on Web, iOS Safari and Capacitor Android
                WebView.  Fixed height h-44 (176 px) preserved so grid
                rows stay uniform. */}
            {/* v79.181 — Founder direction (Feb 2026): "Ensure that
                the background space of images is WHITE only if there
                are gaps, making the ads appear more realistic."
                Removed the blurred filler behind the image and used
                a plain white background so letterboxed portrait/
                landscape photos look like real e-commerce tiles
                (Amazon / Flipkart style) rather than Instagram-esque
                blur cards. */}
            <div className="relative h-44 overflow-hidden bg-white">
                {img ? (
                    <img
                        src={cdnImage(img, 480)}
                        alt={listing.title}
                        loading="lazy"
                        decoding="async"
                        className="relative w-full h-full object-contain object-center [@media(hover:hover)]:group-hover:scale-[1.03] [@media(hover:hover)]:transition-transform [@media(hover:hover)]:duration-300"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-aduku-navy/30 font-display text-3xl">Adukoo</div>
                )}
                {/* v78.11 — DEMO corner ribbon. Founder reported that
                    demo badges in the row below the image were easy to
                    miss when scanning a grid. Surface it as an absolute
                    top-left corner mark on the image itself so every
                    seeded ad is unambiguous at a glance. Sits above
                    affiliate "Sponsored" mark which is rendered on the
                    badge row below (sponsored is mutually exclusive
                    with demo in practice — demos are never affiliate). */}
                {isDemo && (
                    <span
                        data-testid="badge-demo"
                        className="absolute top-1.5 left-1.5 z-10 inline-flex items-center px-2 py-0.5 rounded-md bg-orange-500 text-white text-[10px] font-black uppercase tracking-wider shadow-sm pointer-events-none"
                        title="Seeded demo listing — not a real product"
                    >
                        Demo
                    </span>
                )}
            </div>
            {/* v79.191 — Action bar now RENDERS only for Sponsored /
                Affiliate tiles. For regular tiles it collapsed to an
                empty ~8px strip that ate vertical space with no
                purpose. */}
            {isAffiliate && (
                <div className="px-2.5 sm:px-3 pt-2 pb-1 flex items-center gap-1.5 flex-nowrap">
                    {/* v79.226 — For Sponsored/affiliate deals the "owner"
                        is just the promoter, NOT the seller. So the
                        wishlist should ALWAYS show — including on the
                        promoter's own tiles. Passing ownerId=null skips
                        the "hide for owner" gate inside WishlistButton
                        without affecting regular ads. */}
                    <WishlistButton listingId={listing.id} ownerId={null} size="sm" category={listing.category} />
                    <ShareButton path={`/listing/${listing.id}`} title={listing.title} price={listing.price} variant="sm" listingId={listing.id} listingImage={img} />
                    {/* v79.200 — Founder direction (Feb 2026): "no need
                        to have shared badges — remove completely." All
                        sponsored/affiliate tiles now show a single
                        "Sponsored" pill regardless of the underlying
                        affiliate_network. The shared vs. sponsored
                        distinction was internal-only and confused the
                        public UX. */}
                    <span data-testid="badge-sponsored" className="ml-auto inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-aduku-yellow text-aduku-navy text-[9px] font-extrabold uppercase tracking-wider shrink-0">
                        <Tag className="w-2.5 h-2.5" /> Sponsored
                    </span>
                </div>
            )}
            <div className="px-2.5 sm:px-3 pt-1.5 pb-2 flex flex-col">
                {/* v78.39 — Pill row kept as a fixed-height spacer for
                    layout uniformity. The "SPONSORED" badge is already
                    drawn as a single yellow pill in the action+badge bar
                    above (see the isAffiliate branch around L243); having
                    a SECOND navy SPONSORED pill here was a duplicate
                    that founder caught in the Sponsored Ads rail.
                    Boosted (non-affiliate) still gets its own pill
                    here because boosted has NO badge in the bar above.
                    v78.41 — For affiliate cards the entire pill row is
                    empty (every badge inside is gated on `!isAffiliate`),
                    so we skip the row altogether — reclaims ~22px of
                    vertical space for a less-cramped destination-domain
                    + title block. The 300px tile contract is preserved
                    by `flex-1` on the bottom section. */}
                {/* v79.188 — Founder direction (production, Feb 2026):
                    tile body is pure image + optional single price/MRP
                    line. Removed the entire badge strip (Boosted, New/
                    Used, Business/Individual, AMFI, IRDAI) on regular
                    tiles per "no need to show up boosted badges also"
                    and the earlier "no chips" direction. Sponsored
                    tiles still get the yellow Sponsored pill in the
                    header row above. */}
                {/* v79.191 — Founder direction (Feb 2026): reinstated
                    the Business/Individual + New/Used chip row on
                    ALL categories (with a category-specific exclusion
                    list for New/Used). */}
                {(showAccountBadge || showConditionBadge) && (
                <div className="flex items-center flex-nowrap gap-1 mb-1 h-[18px] overflow-hidden" data-testid={`card-badges-${listing.id}`}>
                    {showAccountBadge && (
                        <span
                            data-testid={isBusiness ? "badge-business" : "badge-individual"}
                            className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider shrink-0 ${isBusiness ? "bg-aduku-navy text-aduku-yellow" : "bg-aduku-bg text-aduku-navy border border-aduku-border"}`}
                        >
                            {isBusiness ? "Business" : "Individual"}
                        </span>
                    )}
                    {showConditionBadge && (
                        <span
                            data-testid={`badge-condition-${listing.condition}`}
                            className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider shrink-0 ${listing.condition === "new" ? "bg-emerald-100 text-emerald-800 border border-emerald-300" : "bg-orange-100 text-orange-800 border border-orange-300"}`}
                        >
                            {listing.condition === "new" ? "New" : "Used"}
                        </span>
                    )}
                    {/* v79.194 — Founder direction (Feb 2026): "Remove
                        Boost badges completely on all ads all sections
                        — public doesn't care. Ad owner sees boost
                        status only on their /my-listings page." Boost
                        pill deleted from the public tile row. */}
                </div>
                )}
                {/* v79.225 — Founder direction (Feb 2026): "show the
                    title on all ads". Earlier v79.187/188 hid the
                    title on tiles entirely; founder reverted that for
                    the MAIN GRID cards (Browse, Shop, Wishlist). We
                    render a 1-line clamped title with a fixed
                    min-height so tile heights stay uniform across
                    mobile + tablet + laptop. Reels / Funboard micro
                    cards use different components and intentionally
                    stay title-less to save space. */}
                {listing.title && (
                    <div
                        data-testid="card-title"
                        title={listing.title}
                        className="font-display font-bold text-aduku-navy text-[13px] sm:text-sm leading-snug line-clamp-1 break-words min-h-[1.15rem] mb-0.5"
                    >
                        {listing.title}
                    </div>
                )}
                {/* v79.72 — Sponsored tile redesign. Founder feedback:
                    "double badging amz etc and title not visible." The
                    previous layout showed the destination domain TWICE
                    (once in the price band as "on amzn.to", again as
                    "Visit amzn.to") and squeezed the title into 2
                    clipped lines that bled a partial 3rd line. Fix:
                    * Drop the price-band domain — the yellow SPONSORED
                      pill in the action bar already signals affiliate.
                    * Promote the title to hero (3 lines instead of 2,
                      slightly larger).
                    * Collapse the domain + new-tab hint into a single
                      compact bottom line: `amzn.to · Opens in a new tab`.
                    Non-affiliate cards are UNCHANGED. */}
                {(() => {
                    // v79.188 — Founder direction (production, Feb 2026):
                    // "No title on tile. Only price / MRP. If price is
                    // also not available, don't show the price row
                    // either." Card body is now image + optional
                    // single line with price + struck MRP. Nothing else.
                    const isJobLike = listing.category === "jobs" || listing.category === "job-seeker";
                    const NO_PRICE_TILE = new Set(["matrimony", "insurance", "mutual-fund", "insurance-distributor", "mutual-fund-distributor", "movies-ott"]);
                    if (NO_PRICE_TILE.has(listing.category)) return null;
                    const rawPrice = isJobLike
                        ? formatCompensationRange(listing.price, listing.meta?.price_max, listing.currency, listing.meta?.compensation_period, listing.category)
                        : formatPrice(listing.price, listing.currency);
                    const priceText = rawPrice === "Contact for price" ? "" : rawPrice;
                    const hasMrpStrike = discountPercent(listing.mrp, listing.price) > 0;
                    const mrpText = hasMrpStrike ? formatPrice(listing.mrp, listing.currency) : "";
                    if (!priceText && !mrpText) return null;
                    return (
                        <div
                            data-testid="card-oneline"
                            className="font-display font-black text-aduku-navy text-base sm:text-lg leading-tight truncate min-h-[1.5rem]"
                        >
                            {priceText && <span className="mr-1">{priceText}</span>}
                            {mrpText && (
                                <span data-testid="card-mrp" className="text-[13px] font-normal text-aduku-navy/45 line-through">
                                    {mrpText}
                                </span>
                            )}
                        </div>
                    );
                })()}
                {/* v79.173 — Naukri-style meta row for jobs: experience band
                    (from form.meta.experience) surfaced as a compact chip
                    right under the pay band so recruiters and candidates
                    can filter by seniority at a glance. Only renders
                    when the employer picked a band; otherwise stays
                    quiet so tiles don't grow taller for jobs without
                    the metadata. */}
                {!isAffiliate && listing.category === "jobs" && listing.meta?.experience && (
                    <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-bold text-aduku-navy/70">
                        <Briefcase className="w-3 h-3" />
                        <span>
                            {(() => {
                                const e = listing.meta.experience;
                                if (e === "fresher") return "Fresher · 0 yrs";
                                if (e === "12+") return "12+ Yrs";
                                return `${e} Yrs`;
                            })()}
                        </span>
                    </div>
                )}
                {/* Title — v79.187 Founder direction (production, Feb
                    2026): "For these sections display ONLY the image,
                    price, and MRP. If any of these data points are
                    not available, they should not be displayed."
                    Title is now hidden on EVERY tile (regular +
                    sponsored). Users still see the full title on the
                    detail page. */}
                {/* Seller + location strip removed for the same
                    reason — regular tiles are now visually identical
                    to sponsored (image + price + MRP), only the
                    header chip row differs. */}
                {false && (
                    <>
                        {/* v79.97 — Guard against empty seller_name.
                            The home-feed projection used to strip
                            `seller_name`, so the tile rendered a bare
                            "by " with no name. Now falls back to the
                            store name and finally hides the row
                            entirely when nothing is known — never a
                            hanging "by ". */}
                        {(() => {
                            const who = listing.seller_name || listing.seller_shop_name;
                            if (!who) return null;
                            // v79.173 — Founder direction: non-merchant
                            // categories (jobs, matrimony, roommates, etc.)
                            // should never call the poster a "Seller". The
                            // tile already just says "by <name>" — safe for
                            // most cases — but for the biggest offenders
                            // (jobs, matrimony) we flip the preposition to
                            // "Posted by" so the framing is unambiguous.
                            const NON_MERCHANT = new Set([
                                "jobs", "job-seeker", "job-profiles",
                                "matrimony", "roommates", "flatmates",
                                "community", "events", "volunteers",
                                "adoption", "lost-and-found",
                            ]);
                            const prefix = NON_MERCHANT.has(listing.category) ? "Posted by" : "by";
                            return (
                                <div className="text-[11px] text-aduku-navy/55 mt-0.5 line-clamp-1 flex items-center gap-1 min-h-[1.05rem]">
                                    {prefix} <span className="font-semibold">{who}</span>
                                    {listing.seller_verified && <VerifiedBadge variant="corner" />}
                                </div>
                            );
                        })()}
                        <div className="flex items-center gap-1 text-[11px] text-aduku-navy/55 mt-0.5 min-h-[1.05rem]">
                            <MapPin className="w-2.5 h-2.5" />
                            <span className="truncate">{listing.city}, {listing.state}</span>
                            {typeof listing.distance_km === "number" && (
                                <span className="ml-auto shrink-0 px-1 py-0 rounded-full bg-aduku-yellow/30 text-aduku-navy text-[9px] font-bold" data-testid="distance-badge">
                                    {listing.distance_km < 1 ? "<1 km" : `${Math.round(listing.distance_km)} km`}
                                </span>
                            )}
                        </div>
                    </>
                )}
            </div>
        </>
    );

    return isAffiliate ? (
        <a
            href={listing.affiliate_url || `/listing/${listing.id}`}
            target="_blank"
            rel="sponsored nofollow noopener noreferrer"
            data-testid={`product-card-${listing.id}`}
            onClick={affiliateClickBeacon}
            onAuxClick={affiliateClickBeacon}
            className={cardClassName}
        >
            {cardBody}
        </a>
    ) : (
        <Link to={`/listing/${listing.id}`} data-testid={`product-card-${listing.id}`} className={cardClassName}>
            {cardBody}
        </Link>
    );
}

export default React.memo(ProductCard);
